// ═══════════════════════════════════════════════════════════════
// PA Grants — Applicant Portal: Damage Sites
// Filtered damage inventory scoped to user's org
// ═══════════════════════════════════════════════════════════════

import { useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Stack, Badge, Button, TextInput, Select,
  Textarea, Modal, SimpleGrid, Table, Loader, Center,
} from '@mantine/core';
import {
  IconPlus, IconSearch, IconRefresh, IconDownload,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { exportToCSV } from '../../../lib/exportUtils';
import { useMyPAOrg } from '../../../hooks/useMyPAOrg';
import { useMyPADamageItems, useMyPAProjects } from '../../../hooks/useMyPAOrgData';
import { usePAGrantsStore } from '../../../store/paGrantsStore';
import { useRoleStore } from '../../../store/roleStore';
import type { DamageCategory } from '../../../types/pagrants';
import { DAMAGE_CATEGORY_LABELS, DAMAGE_CATEGORY_COLORS } from '../../../types/pagrants';

const STATUS_COLORS: Record<string, string> = {
  identified: 'blue', inspected: 'yellow', assigned: 'violet', eligible: 'green', ineligible: 'red',
};

const CATEGORY_OPTIONS = (Object.keys(DAMAGE_CATEGORY_LABELS) as DamageCategory[]).map(k => ({
  value: k, label: DAMAGE_CATEGORY_LABELS[k],
}));

const FACILITY_TYPE_OPTIONS = [
  { value: 'building', label: 'Building' },
  { value: 'road', label: 'Road' },
  { value: 'bridge', label: 'Bridge' },
  { value: 'water_facility', label: 'Water Facility' },
  { value: 'utility', label: 'Utility' },
  { value: 'park', label: 'Park/Recreation' },
  { value: 'other', label: 'Other' },
];

export default function PortalDamageSites() {
  const myOrg = useMyPAOrg();
  const myDamage = useMyPADamageItems();
  const myProjects = useMyPAProjects();
  const { disasters, loadingDamage, createDamageItem, updateDamageItem, fetchDamageItems } = usePAGrantsStore();
  const effectiveRole = useRoleStore(s => s.demoRoleOverride ?? s.myRole);
  const isViewer = effectiveRole?.roleSlug === 'pa_applicant_viewer';

  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editingItem, setEditingItem] = useState<number | null>(null);
  const [form, setForm] = useState({
    facilityName: '', facilityType: '', category: '' as string,
    disasterId: '', projectId: '', damageDescription: '',
    siteAddress: '', siteCity: '', siteCounty: '', siteState: '', siteZip: '',
    estimatedCost: '',
  });

  // Data is fetched by ApplicantPortalLayout — no need to re-fetch here

  const filtered = useMemo(() => {
    if (!search) return myDamage;
    const q = search.toLowerCase();
    return myDamage.filter(d =>
      d.facilityName.toLowerCase().includes(q) ||
      d.damageDescription.toLowerCase().includes(q)
    );
  }, [myDamage, search]);

  async function handleCreate() {
    if (!form.facilityName.trim() || !myOrg) return;
    try {
      await createDamageItem({
        organizationId: String(myOrg.Rid),
        disasterId: form.disasterId,
        facilityName: form.facilityName.trim(),
        facilityType: form.facilityType,
        category: form.category,
        projectId: form.projectId,
        damageDescription: form.damageDescription.trim(),
        siteAddress: form.siteAddress.trim(),
        siteCity: form.siteCity.trim(),
        siteCounty: form.siteCounty.trim(),
        siteState: form.siteState.trim(),
        siteZip: form.siteZip.trim(),
        estimatedCost: form.estimatedCost.replace(/[^0-9.]/g, ''),
        status: 'identified',
      });
      notifications.show({ color: 'green', title: 'Created', message: `Damage site "${form.facilityName}" added.` });
      setShowCreate(false);
      setForm({ facilityName: '', facilityType: '', category: '', disasterId: '', projectId: '', damageDescription: '', siteAddress: '', siteCity: '', siteCounty: '', siteState: '', siteZip: '', estimatedCost: '' });
    } catch {
      notifications.show({ color: 'red', title: 'Error', message: 'Failed to create damage site.' });
    }
  }

  function openEdit(rid: number) {
    const item = myDamage.find(d => d.Rid === rid);
    if (!item) return;
    setForm({
      facilityName: item.facilityName, facilityType: item.facilityType,
      category: item.category, disasterId: item.disasterId, projectId: item.projectId,
      damageDescription: item.damageDescription,
      siteAddress: item.siteAddress, siteCity: item.siteCity,
      siteCounty: item.siteCounty, siteState: item.siteState, siteZip: item.siteZip,
      estimatedCost: item.estimatedCost,
    });
    setEditingItem(rid);
    setShowCreate(true);
  }

  async function handleSaveEdit() {
    if (!editingItem) return;
    try {
      await updateDamageItem(editingItem, {
        facilityName: form.facilityName.trim(),
        facilityType: form.facilityType,
        category: form.category,
        disasterId: form.disasterId,
        projectId: form.projectId,
        damageDescription: form.damageDescription.trim(),
        siteAddress: form.siteAddress.trim(),
        siteCity: form.siteCity.trim(),
        siteCounty: form.siteCounty.trim(),
        siteState: form.siteState.trim(),
        siteZip: form.siteZip.trim(),
        estimatedCost: form.estimatedCost.replace(/[^0-9.]/g, ''),
      });
      notifications.show({ color: 'green', title: 'Updated', message: `Damage site "${form.facilityName}" updated.` });
      setShowCreate(false);
      setEditingItem(null);
      setForm({ facilityName: '', facilityType: '', category: '', disasterId: '', projectId: '', damageDescription: '', siteAddress: '', siteCity: '', siteCounty: '', siteState: '', siteZip: '', estimatedCost: '' });
    } catch {
      notifications.show({ color: 'red', title: 'Error', message: 'Failed to update damage site.' });
    }
  }

  if (loadingDamage) {
    return <Center p="xl"><Loader size="sm" /></Center>;
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={3}>Damage Sites</Title>
        <Group gap="xs">
          <Button variant="light" size="xs" leftSection={<IconDownload size={16} />} onClick={() => {
            exportToCSV('my-damage-sites.csv',
              ['Facility', 'Category', 'Status', 'Address', 'Estimated Cost'],
              filtered.map(d => [d.facilityName, d.category, d.status, [d.siteAddress, d.siteCity, d.siteState].filter(Boolean).join(', '), d.estimatedCost])
            );
          }}>Export CSV</Button>
          <Button variant="subtle" size="xs" leftSection={<IconRefresh size={14} />}
            onClick={() => fetchDamageItems()}>Refresh</Button>
          {!isViewer && (
            <Button size="xs" leftSection={<IconPlus size={14} />}
              onClick={() => setShowCreate(true)}>Report Damage</Button>
          )}
        </Group>
      </Group>

      <TextInput
        placeholder="Search damage sites..."
        leftSection={<IconSearch size={14} />}
        size="xs" value={search}
        onChange={e => setSearch(e.currentTarget.value)}
        style={{ maxWidth: 300 }}
      />

      <Paper withBorder radius="md" style={{ overflow: 'auto' }}>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Facility</Table.Th>
              <Table.Th>Type</Table.Th>
              <Table.Th>Category</Table.Th>
              <Table.Th>Project</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Description</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Est. Repair</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filtered.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={7}>
                  <Text c="dimmed" ta="center" py="md" size="sm">No damage sites reported</Text>
                </Table.Td>
              </Table.Tr>
            ) : filtered.map(d => (
              <Table.Tr key={d.Rid} style={{ cursor: 'pointer' }} onClick={() => openEdit(d.Rid)}>
                <Table.Td><Text size="sm" fw={600}>{d.facilityName}</Text></Table.Td>
                <Table.Td><Text size="sm">{d.facilityType}</Text></Table.Td>
                <Table.Td>
                  {d.category && (
                    <Badge size="xs" variant="light"
                      style={{ backgroundColor: DAMAGE_CATEGORY_COLORS[d.category as DamageCategory] + '20',
                               color: DAMAGE_CATEGORY_COLORS[d.category as DamageCategory] }}>
                      {DAMAGE_CATEGORY_LABELS[d.category as DamageCategory] ?? d.category}
                    </Badge>
                  )}
                </Table.Td>
                <Table.Td>
                  {(() => {
                    const proj = myProjects.find(p => String(p.Rid) === d.projectId);
                    return proj ? <Badge size="xs" variant="light">{proj.projectNumber}</Badge>
                      : <Text size="xs" c="dimmed">Unassigned</Text>;
                  })()}
                </Table.Td>
                <Table.Td>
                  <Badge size="xs" color={STATUS_COLORS[d.status] || 'gray'} variant="dot">
                    {d.status}
                  </Badge>
                </Table.Td>
                <Table.Td><Text size="sm" lineClamp={1}>{d.damageDescription}</Text></Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>
                  <Text size="sm">
                    {d.estimatedCost ? '$' + parseFloat(d.estimatedCost).toLocaleString() : '--'}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>

      {/* Create modal */}
      <Modal opened={showCreate} onClose={() => { setShowCreate(false); setEditingItem(null); }}
        title={editingItem ? 'Edit Damage Site' : 'Report Damage Site'} size="lg">
        <Stack gap="sm">
          <TextInput label="Facility Name" required value={form.facilityName}
            onChange={e => setForm(f => ({ ...f, facilityName: e.currentTarget.value }))} />
          <SimpleGrid cols={2} spacing="sm">
            <Select label="Facility Type" data={FACILITY_TYPE_OPTIONS}
              value={form.facilityType || null} onChange={v => setForm(f => ({ ...f, facilityType: v ?? '' }))} />
            <Select label="Category" data={CATEGORY_OPTIONS}
              value={form.category || null} onChange={v => setForm(f => ({ ...f, category: v ?? '' }))} />
            <Select label="Disaster" data={disasters.map(d => ({ value: String(d.Rid), label: d.disasterName }))}
              value={form.disasterId || null} onChange={v => setForm(f => ({ ...f, disasterId: v ?? '' }))}
              searchable />
            <Select label="Link to Project" data={myProjects.map(p => ({ value: String(p.Rid), label: `${p.projectNumber} — ${p.projectTitle}` }))}
              value={form.projectId || null} onChange={v => setForm(f => ({ ...f, projectId: v ?? '' }))}
              placeholder="Optional" searchable clearable />
            <TextInput label="Est. Repair Cost" value={form.estimatedCost}
              onChange={e => setForm(f => ({ ...f, estimatedCost: e.currentTarget.value }))}
              placeholder="$0" />
          </SimpleGrid>
          <Textarea label="Damage Description" value={form.damageDescription}
            onChange={e => setForm(f => ({ ...f, damageDescription: e.currentTarget.value }))}
            rows={3} />
          <SimpleGrid cols={2} spacing="sm">
            <TextInput label="Address" value={form.siteAddress}
              onChange={e => setForm(f => ({ ...f, siteAddress: e.currentTarget.value }))} />
            <TextInput label="City" value={form.siteCity}
              onChange={e => setForm(f => ({ ...f, siteCity: e.currentTarget.value }))} />
            <TextInput label="County" value={form.siteCounty}
              onChange={e => setForm(f => ({ ...f, siteCounty: e.currentTarget.value }))} />
            <TextInput label="State" value={form.siteState}
              onChange={e => setForm(f => ({ ...f, siteState: e.currentTarget.value }))} />
          </SimpleGrid>
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={() => { setShowCreate(false); setEditingItem(null); }}>Cancel</Button>
            <Button onClick={editingItem ? handleSaveEdit : handleCreate}>
              {editingItem ? 'Save Changes' : 'Report Damage'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
